/**
 * What the film and the 3D vehicle (1.137.0, `features/thread/vehicle3d.ts`)
 * must agree on, in a file without three.js: the film places the square and
 * sizes its canvas, and loading three.js for a few numbers would put it into
 * every page.
 */

/**
 * The square looks at least this far off straight down. The flat film looks
 * straight down, and a model seen from there is a roof.
 */
export const LEAST_TILT = (40 * Math.PI) / 180

/** Half the width the square shows, in grid units of the drawings: the longest model and its shadow fit. */
export const HALF_VIEW = 46

/**
 * The side of the square in units of the base film (1280 x 720). From use
 * (1.139.0): "etwas größer" -- 104 became 128, a quarter more. The canvas
 * is drawn at this size times the film's scale, so the model stays sharp;
 * `HALF_VIEW` is unchanged, the model fills the square as before.
 */
export const SPRITE_BASE = 128

/**
 * The 3D vehicle's square on the film's picture, standing on the vehicle's
 * point, in units of the base film. From use (1.139.0): "ohne weiße Scheibe,
 * etwas größer" -- the model stands on the map with its own shadow (the
 * `ShadowMaterial` ground in `vehicle3d.ts`); the white disc with its ring
 * belongs to the 2D symbol and made the model one. Here and not in
 * `routeFilm.ts`, so that `fahrzeug3d.mjs` measures the very stamp the film
 * uses without bundling the film.
 */
export function stampSolid(pen: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, picture: CanvasImageSource, px: number, py: number): void {
  pen.drawImage(picture, px - SPRITE_BASE / 2, py - SPRITE_BASE / 2, SPRITE_BASE, SPRITE_BASE)
}

/** The tilt the square is seen with, for a film camera's tilt. */
export function viewTilt(tilt: number): number {
  return Math.max(tilt, LEAST_TILT)
}

/**
 * How much the square's camera squeezes the ground in the picture's height:
 * the sine of its elevation, never under 0.2. `vehicle3d.ts` turns the model
 * with it, and `lengthOnScreen()` measures the model with it; one number for
 * both, or the nose would stand beside the place it was put.
 */
export function groundSqueeze(tilt: number): number {
  return Math.max(0.2, Math.sin(Math.PI / 2 - viewTilt(tilt)))
}

/** Units of the base film per grid unit of a model, across the square. */
export const GRID_PX = SPRITE_BASE / (2 * HALF_VIEW)

/** Units of the base film per grid unit of a model's length, the model turned to `onScreen`. */
export function lengthOnScreen(onScreen: number, tilt: number): number {
  return GRID_PX / Math.hypot(Math.cos(onScreen), Math.sin(onScreen) / groundSqueeze(tilt))
}

/** How far a model reaches ahead of and behind its middle, in grid units. */
export interface ModelExtent {
  front: number
  rear: number
}

/**
 * How far the vehicle's road is rounded, as a share of the model's length
 * across the square (1.144.0). From use, after 1.143.0 had put nose and rear
 * on the line: "es scheint als würde das Heck an der Straße kleben, das Auto
 * ist neben der Straße", then "das ganze Fahrzeug sollte der Straße folgen.
 * wie ein echtes Auto", and decided: round the corners. On the map the model
 * is kilometres long, and no rigid body lies on a bend shorter than itself;
 * a real car stands across a corner too, it is only small against it.
 *
 * Measured in `tourscript.ts` on a right angle, the whole body's farthest
 * point from the line while driving through: 0.29 lengths with nose and rear
 * on it (1.143.0), 0.35 with nose and middle (a draft of 1.144.0), 0.19 with
 * the middle on a road rounded by half a length (0.20 since 1.146.0 rounds it
 * exactly, and it turns 2.8 degrees a step instead of 7.4). Less rounding turns harder
 * at the corner and lets the ends out further (0.25 at a quarter), more cuts
 * the corner (0.29 at three quarters).
 */
export const SEAT_ROUND = 0.5

/**
 * Where the 3D vehicle stands and which way it looks (1.143.0, 1.144.0).
 * From use: "die Mitte der Front des Fahrzeugs soll immer in der Mitte der
 * Straße sein ... vor allem in Kurven". Until 1.142.0 the model's middle stood
 * on the script's point and looked along its calm heading, which in a bend
 * points past the road.
 *
 * `way` is the road on the picture around the vehicle, in units of the base
 * film, and `at` is how far along it the script's point lies. The road is
 * rounded (`SEAT_ROUND`, a bell over its length), the vehicle's middle stands
 * on the rounded road half a model behind the point, and it looks along that
 * road there. On a straight road that puts the nose exactly on the point. A
 * way that ends (start or end of a section) is carried on straight; without
 * any stretch at all, `guess` (the calm heading) decides, the nose on the
 * point. The model's length on the picture depends on its direction, so the
 * place and the direction are found together, in a few passes.
 */
export function seatOnWay(
  way: [number, number][],
  at: number,
  extent: ModelExtent,
  tilt: number,
  guess: number,
  round = SEAT_ROUND,
): { x: number; y: number; onScreen: number } {
  const length = extent.front + extent.rear
  const road = roundedRoad(way, round * length * GRID_PX)
  if (road === null) {
    const [nx, ny] = way[0]!
    const k = extent.front * lengthOnScreen(guess, tilt)
    return { x: nx - Math.cos(guess) * k, y: ny - Math.sin(guess) * k, onScreen: guess }
  }
  let onScreen = guess
  let middle: [number, number] = road(at).at
  for (let pass = 0; pass < 6; pass++) {
    const here = road(at - (length / 2) * lengthOnScreen(onScreen, tilt))
    middle = here.at
    onScreen = here.heading
  }
  // The square's middle is the model's origin, not the middle of its length.
  const shift = ((extent.front - extent.rear) / 2) * lengthOnScreen(onScreen, tilt)
  return { x: middle[0] - Math.cos(onScreen) * shift, y: middle[1] - Math.sin(onScreen) * shift, onScreen }
}

/** A straight piece of the road, from `from` to `to` along it (the rays past the ends reach to infinity), running in `direction`. */
interface Piece {
  from: number
  to: number
  /** Where the piece is at `anchor` along the road: its start, or its end for the ray before the way. */
  point: [number, number]
  anchor: number
  direction: [number, number]
}

/**
 * The road along its length, rounded over a bell of `sigma` and carried on
 * straight past both ends -- the place and the direction at `at` -- or
 * `null` for a way without a stretch.
 *
 * The bell is integrated exactly, piece by piece (1.146.0). From use, with a
 * film: "warum wackelt/ zittert das Fahrzeug?" 1.144.0 summed the road at 37
 * fixed places a sixth of `sigma` apart and took the direction as the
 * difference of two such sums a pixel apart -- which is the mean direction of
 * the pieces lying under those 37 places. A GPS track on the picture is a
 * zigzag of short pieces, and the vehicle moves about half that spacing per
 * frame, so odd and even frames met two different sets of zigzags: the model
 * turned to and fro by several degrees, frame by frame (measured in the film
 * and in `tourscript.ts`: up to 9.8 degrees, against 0.05 exactly).
 */
function roundedRoad(way: [number, number][], sigma: number): ((at: number) => { at: [number, number]; heading: number }) | null {
  const sofar = [0]
  for (let i = 1; i < way.length; i++) sofar.push(sofar[i - 1]! + Math.hypot(way[i]![0] - way[i - 1]![0], way[i]![1] - way[i - 1]![1]))
  const total = sofar[sofar.length - 1]!
  if (way.length < 2 || total < 1e-6) return null
  const pieces: Piece[] = []
  for (let i = 1; i < way.length; i++) {
    const size = sofar[i]! - sofar[i - 1]!
    if (size < 1e-9) continue
    const a = way[i - 1]!
    const b = way[i]!
    pieces.push({ from: sofar[i - 1]!, to: sofar[i]!, point: a, anchor: sofar[i - 1]!, direction: [(b[0] - a[0]) / size, (b[1] - a[1]) / size] })
  }
  const first = pieces[0]!
  const last = pieces[pieces.length - 1]!
  pieces.unshift({ from: -Infinity, to: 0, point: way[0]!, anchor: 0, direction: first.direction })
  pieces.push({ from: total, to: Infinity, point: way[way.length - 1]!, anchor: total, direction: last.direction })
  if (sigma <= 0) {
    return (at) => {
      const piece = pieces.find((p) => at < p.to) ?? pieces[pieces.length - 1]!
      const s = at - piece.anchor
      return { at: [piece.point[0] + piece.direction[0] * s, piece.point[1] + piece.direction[1] * s], heading: Math.atan2(piece.direction[1], piece.direction[0]) }
    }
  }
  return (at) => {
    let x = 0
    let y = 0
    let dx = 0
    let dy = 0
    for (const piece of pieces) {
      const zFrom = (piece.from - at) / sigma
      const zTo = (piece.to - at) / sigma
      if (zTo < -8 || zFrom > 8) continue
      // The bell's share of this piece, and its mean distance along it.
      const share = normalBelow(zTo) - normalBelow(zFrom)
      const along = (at - piece.anchor) * share + sigma * (normalDensity(zFrom) - normalDensity(zTo))
      x += piece.point[0] * share + piece.direction[0] * along
      y += piece.point[1] * share + piece.direction[1] * along
      dx += piece.direction[0] * share
      dy += piece.direction[1] * share
    }
    return { at: [x, y], heading: Math.atan2(dy, dx) }
  }
}

/** The standard normal density, 0 at either infinity. */
function normalDensity(z: number): number {
  return Number.isFinite(z) ? Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI) : 0
}

/** The share of the standard normal below `z`. */
function normalBelow(z: number): number {
  if (z === Infinity) return 1
  if (z === -Infinity) return 0
  return 0.5 * (1 + erf(z / Math.SQRT2))
}

/**
 * The error function after Abramowitz and Stegun 7.1.26, within 1.5e-7:
 * JavaScript has none, and a polynomial times `exp` is smooth, so the road
 * built on it does not step either.
 */
function erf(x: number): number {
  const sign = Math.sign(x)
  const a = Math.abs(x)
  const t = 1 / (1 + 0.3275911 * a)
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-a * a)
  return sign * y
}

let webgl: boolean | null = null

/**
 * Whether this browser can draw the 3D vehicle: a WebGL context, asked
 * once. Without it the switch does not stand -- a choice that changes
 * nothing is a broken control.
 */
export function canDraw3d(): boolean {
  if (webgl === null) {
    try {
      const canvas = document.createElement('canvas')
      webgl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) !== null
    } catch {
      webgl = false
    }
  }
  return webgl
}
